import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import useSWR, { mutate } from "swr";
import { staticOptions } from "lib/core/apiClient";
import {
  clearCheckoutPlan,
  fetchCheckoutPlans,
  selectCheckoutPlan,
  subscribeCheckout,
} from "lib/core/subscriptionMethods";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import { useLocale } from "lib/hooks/useLocale";
import { readAppliedSubscription } from "lib/utils/subscriptions/parseSubscriptions";
import {
  CheckoutPlansResult,
  PurchaseMode,
  SubscriptionContextType,
  SubscriptionState,
} from "lib/types/subscriptions";
import { AddOnType } from "lib/types/checkout";
import { errorToast } from "lib/utils/toasters";

type Action =
  | { type: "PLANS_LOADING" }
  | { type: "PLANS_LOADED"; payload: CheckoutPlansResult; appliedPlanId: string | null }
  | { type: "PLANS_UNAVAILABLE" }
  | { type: "SWITCH_START" }
  | { type: "SWITCH_END" }
  | { type: "SERVER_CLEAR_DONE" }
  | {
      type: "SET_MODE";
      payload: { mode: PurchaseMode; selectedPlanId: string | null; subscriptionId?: string };
    };

export const initialSubscriptionState: SubscriptionState = {
  isEligible: false,
  mode: "PAY_ONCE",
  subscriptionId: null,
  plans: [],
  selectedPlanId: null,
  isPlansLoading: false,
  hasLoadedPlans: false,
  isSwitching: false,
  pendingServerClear: false,
};

function reducer(state: SubscriptionState, action: Action): SubscriptionState {
  switch (action.type) {
    case "PLANS_LOADING":
      return { ...state, isPlansLoading: true };

    case "PLANS_LOADED": {
      const { subscriptionId, plans } = action.payload;
      const restored =
        action.appliedPlanId && plans.some((plan) => plan.id === action.appliedPlanId)
          ? action.appliedPlanId
          : null;
      const isFirstLoad = !state.hasLoadedPlans;
      const isStillOffered = Boolean(
        state.selectedPlanId && plans.some((plan) => plan.id === state.selectedPlanId),
      );

      const nextSelectedPlanId = isFirstLoad ? restored : isStillOffered ? state.selectedPlanId : null;

      const lostLiveSelection = state.mode === "SUBSCRIBE" && !isFirstLoad && !nextSelectedPlanId;

      return {
        ...state,
        hasLoadedPlans: true,
        isPlansLoading: false,
        isEligible: plans.length > 0,
        subscriptionId,
        plans,
        selectedPlanId: nextSelectedPlanId,
        mode: isFirstLoad
          ? restored
            ? "SUBSCRIBE"
            : state.mode
          : lostLiveSelection
          ? "PAY_ONCE"
          : state.mode,
        pendingServerClear: state.pendingServerClear || lostLiveSelection,
      };
    }

    case "PLANS_UNAVAILABLE":
      return {
        ...initialSubscriptionState,
        pendingServerClear: state.pendingServerClear || state.mode === "SUBSCRIBE",
      };

    case "SWITCH_START":
      return { ...state, isSwitching: true };

    case "SWITCH_END":
      return { ...state, isSwitching: false };

    case "SERVER_CLEAR_DONE":
      return { ...state, pendingServerClear: false };

    case "SET_MODE":
      return {
        ...state,
        mode: action.payload.mode,
        selectedPlanId: action.payload.selectedPlanId,
        subscriptionId: action.payload.subscriptionId ?? state.subscriptionId,
      };

    default:
      return state;
  }
}

/** Exported for the dev preview page only. Consume via useSubscriptionContext. */
export const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const PLANS_RETRY_LIMIT = 3;
const PLANS_RETRY_INTERVAL_MS = 1500;
/* Trailing wait after the cart stops changing, so one action costs one plans fetch. */
const CART_SETTLE_MS = 250;

const IS_CART_PAGE = new URLSearchParams(window.location.search).get("page")?.toLowerCase() === "cart";
/* Storefronts can link a "Subscribe & Save" button to checkout with ?purchase_mode=subscribe. */
const WANTS_SUBSCRIBE =
  new URLSearchParams(window.location.search).get("purchase_mode")?.toLowerCase() === "subscribe";

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialSubscriptionState);
  const { t } = useLocale();

  const {
    state: {
      checkoutId,
      isC2P,
      checkoutItems,
      appliedCartAddOns,
      billing,
      rawCheckoutObject,
      fetchedCheckoutId,
      checkoutFetchSeq,
    },
    actions: { updateCheckoutBasedOnCheckoutResponse },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { user },
  } = useUserContext();
  const customerId: string = user?.uid ?? "";
  const [active, setActive] = useState({ checkoutId: "", customerId: "" });

  const baselineFetchSeq = useRef<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      baselineFetchSeq.current = checkoutFetchSeq;
      setActive({ checkoutId: "", customerId: "" });
      return;
    }

    const hasFetchedForThisCustomer =
      baselineFetchSeq.current === null || checkoutFetchSeq > baselineFetchSeq.current;

    if (fetchedCheckoutId && fetchedCheckoutId === checkoutId && hasFetchedForThisCustomer) {
      setActive({ checkoutId: fetchedCheckoutId, customerId });
    }
  }, [isAuthenticated, customerId, checkoutId, fetchedCheckoutId, checkoutFetchSeq]);

  const activeCheckoutId = active.customerId === customerId ? active.checkoutId : "";
  const rawCartSignature = useMemo(() => {
    const itemPart = Array.isArray(checkoutItems)
      ? checkoutItems
          .map((item: any) => {
            const addOns = (item?.appliedItemAddOns ?? [])
              .map((addOn: AddOnType) => addOn?.addon_id)
              .sort()
              .join(",");

            return `${item?.variant_id ?? item?.item_id}:${item?.quantity}:${addOns}`;
          })
          .sort()
          .join("|")
      : "";

    if (!itemPart) return "";

    const cartAddOnPart = (appliedCartAddOns ?? [])
      .map((addOn: AddOnType) => addOn?.addon_id)
      .sort()
      .join(",");

    return `${itemPart}#${cartAddOnPart}#${billing?.shipping ?? ""}`;
  }, [checkoutItems, appliedCartAddOns, billing?.shipping]);

  const [cartSignature, setCartSignature] = useState(rawCartSignature);

  useEffect(() => {
    if (rawCartSignature === cartSignature) return;

    if (!cartSignature) {
      setCartSignature(rawCartSignature);

      return;
    }

    const timer = setTimeout(() => setCartSignature(rawCartSignature), CART_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [rawCartSignature, cartSignature]);

  const gatesPassed = Boolean(
    !IS_CART_PAGE &&
      isAuthenticated &&
      activeCheckoutId &&
      !isC2P &&
      cartSignature &&
      merchant?.migrationConfig?.kratosPaymentFlow &&
      merchant?.subscriptionsEnabled,
  );

  const appliedForCart = useRef<string>("");
  const applyingForCart = useRef<string>("");

  /** The applied subscription, as the server records it on the checkout. */
  const appliedSubscription = useMemo(() => readAppliedSubscription(rawCheckoutObject), [rawCheckoutObject]);

  const plansKey = gatesPassed ? `/checkout/${activeCheckoutId}/plans#${cartSignature}` : null;

  const {
    data: plansResult,
    isValidating,
    error: plansError,
  } = useSWR<CheckoutPlansResult>(plansKey, () => fetchCheckoutPlans(activeCheckoutId), {
    ...staticOptions,
    keepPreviousData: true,
    shouldRetryOnError: true,
    onErrorRetry: (error: any, _key, _config, revalidate, { retryCount }) => {
      if (!error?.response || retryCount >= PLANS_RETRY_LIMIT) return;
      setTimeout(() => revalidate({ retryCount }), PLANS_RETRY_INTERVAL_MS);
    },
  });

  useEffect(() => {
    if (!gatesPassed) {
      dispatch({ type: "PLANS_UNAVAILABLE" });
      return;
    }
    if (isValidating && !plansResult) {
      dispatch({ type: "PLANS_LOADING" });
      return;
    }
    if (plansError && !plansResult) {
      dispatch({ type: "PLANS_UNAVAILABLE" });
      return;
    }
    if (plansResult) {
      if (appliedSubscription && !appliedForCart.current) appliedForCart.current = cartSignature;

      dispatch({
        type: "PLANS_LOADED",
        payload: plansResult,
        appliedPlanId: appliedSubscription?.planId ?? null,
      });
    }
  }, [gatesPassed, plansResult, plansError, isValidating, appliedSubscription, cartSignature]);

  const refreshCheckout = useCallback(
    (response: any) => {
      if (response) updateCheckoutBasedOnCheckoutResponse(response);
      mutate(`/checkout/${checkoutId}/payments`);
    },
    [checkoutId, updateCheckoutBasedOnCheckoutResponse],
  );

  useEffect(() => {
    if (!state.pendingServerClear || !activeCheckoutId || !appliedSubscription) return;

    let cancelled = false;

    clearCheckoutPlan(activeCheckoutId)
      .then((response) => {
        if (!cancelled) refreshCheckout(response);
      })
      .catch((error) => {
        // A subscription the server already cleared 4xxs here, which is expected.
        console.error("Clearing an invalidated subscription failed", error);
      })
      .finally(() => {
        if (!cancelled) dispatch({ type: "SERVER_CLEAR_DONE" });
      });

    return () => {
      cancelled = true;
    };
  }, [state.pendingServerClear, activeCheckoutId, appliedSubscription, refreshCheckout]);

  useEffect(() => {
    if (state.mode !== "SUBSCRIBE" || !state.subscriptionId || !activeCheckoutId) return;
    if (isValidating || state.isSwitching) return;
    if (appliedForCart.current === cartSignature || applyingForCart.current === cartSignature) return;

    const target = cartSignature;
    applyingForCart.current = target;

    let cancelled = false;

    subscribeCheckout(activeCheckoutId, state.subscriptionId)
      .then((response) => {
        if (cancelled) return;

        appliedForCart.current = target;
        refreshCheckout(response);
      })
      .catch((error) => {
        console.error("Re-applying the subscription failed", error);
        if (!cancelled) errorToast(t("subscription_mode_switch_failed"));
      })
      .finally(() => {
        if (applyingForCart.current === target) applyingForCart.current = "";
      });

    return () => {
      cancelled = true;
    };
  }, [
    cartSignature,
    isValidating,
    state.mode,
    state.subscriptionId,
    state.isSwitching,
    activeCheckoutId,
    refreshCheckout,
    t,
  ]);

  const setMode = useCallback(
    async (nextMode: PurchaseMode) => {
      if (state.isSwitching || nextMode === state.mode) return;
      if (!state.isEligible) return;
      if (nextMode === "SUBSCRIBE" && !state.subscriptionId) return;

      if (!checkoutId) return;

      const previous = { mode: state.mode, selectedPlanId: state.selectedPlanId };

      dispatch({ type: "SWITCH_START" });
      try {
        if (nextMode === "SUBSCRIBE") {
          if (!state.plans.length || !state.subscriptionId) {
            dispatch({ type: "SET_MODE", payload: previous });
            errorToast(t("subscription_no_longer_available"));
            return;
          }

          const freshPlans = state.plans;
          const response = await subscribeCheckout(checkoutId, state.subscriptionId);
          appliedForCart.current = cartSignature;
          const reported = readAppliedSubscription(response);
          const applied =
            reported?.planId ??
            freshPlans.find((plan) => plan.isRecommended)?.id ??
            freshPlans[0]?.id ??
            null;

          dispatch({
            type: "SET_MODE",
            payload: {
              mode: "SUBSCRIBE",
              selectedPlanId: applied,
              subscriptionId: reported?.subscriptionId,
            },
          });
          refreshCheckout(response);
          return;
        }

        const response = await clearCheckoutPlan(checkoutId);
        dispatch({ type: "SET_MODE", payload: { mode: "PAY_ONCE", selectedPlanId: null } });
        refreshCheckout(response);
      } catch (error) {
        // Roll back, or the tab says Subscribe beside one-time pricing.
        console.error("Subscription mode switch failed", error);
        dispatch({ type: "SET_MODE", payload: previous });
        errorToast(t("subscription_mode_switch_failed"));
      } finally {
        dispatch({ type: "SWITCH_END" });
      }
    },
    [state, checkoutId, refreshCheckout, t],
  );

  const selectPlan = useCallback(
    async (planId: string) => {
      if (state.isSwitching) return;
      if (planId === state.selectedPlanId) return;
      if (!state.plans.some((plan) => plan.id === planId)) return;

      if (!checkoutId) return;

      const previous = { mode: state.mode, selectedPlanId: state.selectedPlanId };
      const plan = state.plans.find((candidate) => candidate.id === planId);
      const needsProgramSwitch = Boolean(
        plan && state.subscriptionId && plan.subscriptionId !== state.subscriptionId,
      );

      dispatch({ type: "SWITCH_START" });
      try {
        if (needsProgramSwitch && plan) {
          await subscribeCheckout(checkoutId, plan.subscriptionId);
        }

        const response = await selectCheckoutPlan(checkoutId, planId);
        appliedForCart.current = cartSignature;
        dispatch({
          type: "SET_MODE",
          payload: {
            mode: "SUBSCRIBE",
            selectedPlanId: planId,
            subscriptionId: needsProgramSwitch ? plan?.subscriptionId : undefined,
          },
        });
        refreshCheckout(response);
      } catch (error) {
        console.error("Plan selection failed", error);
        dispatch({ type: "SET_MODE", payload: previous });
        errorToast(t("subscription_plan_select_failed"));
      } finally {
        dispatch({ type: "SWITCH_END" });
      }
    },
    [state, checkoutId, refreshCheckout, t],
  );

  const requestedSubscribe = useRef(false);

  useEffect(() => {
    if (!WANTS_SUBSCRIBE || requestedSubscribe.current) return;
    if (!state.hasLoadedPlans || !state.isEligible || !state.subscriptionId) return;

    /* Once only: after this the shopper owns the tab, including switching back. */
    requestedSubscribe.current = true;

    if (state.mode !== "SUBSCRIBE") setMode("SUBSCRIBE");
  }, [state.hasLoadedPlans, state.isEligible, state.subscriptionId, state.mode, setMode]);

  const value = useMemo(() => ({ state, actions: { setMode, selectPlan } }), [state, setMode, selectPlan]);

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscriptionContext = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context) return context;

  return {
    state: initialSubscriptionState,
    actions: {
      setMode: async () => undefined,
      selectPlan: async () => undefined,
    },
  };
};
